import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BlogPreviewComponent as BlogPreviewComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { usePageContext } from '../contexts/PageContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getFontFamily } from '../utils/fontLoader';
import { blogApi } from '../api/client';
import type { BlogPost } from '../schemas/validation';

interface BlogPreviewComponentProps {
  component: BlogPreviewComponentType;
}

export const BlogPreviewComponent: React.FC<BlogPreviewComponentProps> = ({ component }) => {
  const { data } = component;
  const { siteId } = usePageContext();
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [blogBasePath, setBlogBasePath] = useState('/blog');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!siteId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const [blogRes, postsRes] = await Promise.all([
          blogApi.get(siteId),
          blogApi.posts.list(siteId, {
            limit: data.maxPosts ?? 3,
            status: 'published',
          }),
        ]);
        setBlogBasePath(blogRes.blog?.basePath || '/blog');
        setPosts(postsRes.posts || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [siteId, data.maxPosts]);

  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const cc = (data as any).customColors;
  const fallback = themeStyles.blogPreview || themeStyles.stats || {};
  const backgroundColor = cc?.background || fallback.background || '#f9fafb';
  const titleColor = cc?.title || fallback.titleColor || '#111827';
  const subtitleColor = cc?.subtitle || fallback.subtitleColor || '#6b7280';
  const cardBg = cc?.cardBackground || fallback.cardBackground || '#ffffff';
  const cardTitleColor = cc?.cardTitle || fallback.cardTitle || '#111827';
  const cardExcerptColor = cc?.cardExcerpt || fallback.cardExcerpt || '#4b5563';
  const linkColor = cc?.linkColor || fallback.linkColor || '#3b82f6';

  const maxPosts = data.maxPosts ?? 3;
  const showExcerpt = data.showExcerpt ?? true;
  const showDate = data.showDate ?? true;
  const showFeaturedImage = data.showFeaturedImage ?? true;
  const linkText = data.linkText || 'Read more';

  const displayPosts = posts.slice(0, maxPosts);

  const gridClasses =
    displayPosts.length >= 3
      ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
      : displayPosts.length === 2
        ? 'grid grid-cols-1 md:grid-cols-2 gap-8'
        : 'grid grid-cols-1 max-w-md mx-auto';

  return (
    <section className="py-16 md:py-20" style={{ backgroundColor }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(data.title || data.subtitle) && (
          <div className="text-center mb-12">
            {data.title && (
              <h2
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ color: titleColor, ...headingFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
              />
            )}
            {data.subtitle && (
              <p
                className="text-base opacity-80"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
              />
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12" style={{ color: subtitleColor }}>
            Loading posts...
          </div>
        ) : displayPosts.length === 0 ? (
          <p className="text-center opacity-70" style={{ color: subtitleColor }}>
            No posts yet.
          </p>
        ) : (
          <div className={gridClasses}>
            {displayPosts.map(post => {
              const postUrl = `${blogBasePath.replace(/\/$/, '')}/${post.slug || ''}`;
              const formattedDate = post.publishedAt
                ? new Date(post.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '';

              return (
                <Link
                  key={post.id}
                  to={postUrl}
                  className="block rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                  style={{ backgroundColor: cardBg }}
                >
                  {showFeaturedImage && post.featuredImageUrl && (
                    <img
                      src={post.featuredImageUrl}
                      alt={post.title || 'Post'}
                      className="w-full h-48 object-cover rounded-t-lg"
                      loading="lazy"
                    />
                  )}
                  <div className="p-6">
                    {showDate && formattedDate && (
                      <span
                        className="text-xs opacity-75 block"
                        style={{ color: cardExcerptColor }}
                      >
                        {formattedDate}
                      </span>
                    )}
                    <h3
                      className="text-lg font-semibold mt-1"
                      style={{ color: cardTitleColor, ...headingFontStyle }}
                    >
                      {post.title || 'Untitled'}
                    </h3>
                    {showExcerpt && post.excerpt && (
                      <p
                        className="text-sm mt-2"
                        style={{ color: cardExcerptColor, ...bodyFontStyle }}
                      >
                        {post.excerpt}
                      </p>
                    )}
                    <span
                      className="inline-block mt-3 text-sm font-medium"
                      style={{ color: linkColor }}
                    >
                      {linkText} →
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
